import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { when } from 'lit/directives/when.js';
import { baseStyles } from '../../../styles/base';
import { dlStyles } from '../../../styles/dl';
import { StateController } from '../../../state';
import type { Package } from '../entities';

export type SidebarRenderMode =
  | 'short-content'
  | 'short-sidebar'
  | 'full-sidebar';

/**
 * Sidebars are rendered based on the readme content length
 * - No readme => Short sidebar in content, mini sidebar
 * - Short readme => Mini sidebar in content, short sidebar
 * - Long readme => Full sidebar (all elements)
 */

@customElement('umb-market-package-sidebar')
export class UmbMarketPackageSidebar extends LitElement {
  @property({ type: Object })
  package?: Package;

  @property({ type: String })
  variant: SidebarRenderMode = 'full-sidebar';

  #renderUpdatedInfo() {
    return html`<umb-market-package-sidebar-updated-info
      .package=${this.package}
    ></umb-market-package-sidebar-updated-info>`;
  }

  #renderBadges() {
    return html` <umb-market-package-sidebar-badges .package=${this.package}>
    </umb-market-package-sidebar-badges>`;
  }

  #renderStatistics() {
    return html`${when(
      this.package?.repositoryStatistics,
      () => html` <umb-market-package-sidebar-repo-statistics
        .model=${this.package?.repositoryStatistics}
      ></umb-market-package-sidebar-repo-statistics>`
    )}`;
  }

  #renderInstallInfo() {
    return html` <umb-market-package-sidebar-install-info
      .package=${this.package}
    ></umb-market-package-sidebar-install-info>`;
  }

  #renderCategoriesAndLinks() {
    return html`<div id="LinksCategory">
      <umb-market-package-sidebar-links
        .package=${this.package}
      ></umb-market-package-sidebar-links
      >${when(
        this.package?.category && !StateController.isEmbedView,
        () => html` <umb-market-package-sidebar-category
          .category=${this.package?.category?.id}
          .altCategory=${this.package?.alternateCategory?.id}
        ></umb-market-package-sidebar-category>`
      )}
    </div>`;
  }

  render() {
    if (!this.package) return null;

    return html`<aside>
      ${choose(this.variant, [
        [
          'full-sidebar',
          () =>
            html`${this.#renderUpdatedInfo()}${this.#renderBadges()}${this.#renderStatistics()}${this.#renderInstallInfo()}${this.#renderCategoriesAndLinks()}`,
        ],
        [
          'short-sidebar',
          () =>
            html`${this.#renderUpdatedInfo()}${this.#renderBadges()}${this.#renderStatistics()}${this.#renderInstallInfo()}`,
        ],
        ['short-content', () => html`${this.#renderCategoriesAndLinks()}`],
      ])}
    </aside>`;
  }

  static styles = [
    baseStyles,
    dlStyles,
    css`
      :host {
        display: block;
        margin-top: var(--l-spacer);
        --border-radius: 22px;
        --badge-font-size: 18px;
        --badge-height: 36px;
        --badge-font-weight: 300;
      }

      aside {
        position: relative;
        border-radius: var(--border-radius);
        border: 1px solid var(--color-border);
      }

      aside > * {
        padding: var(--m-spacer);
        position: relative;
      }

      @media (min-width: 767px) {
        aside > * {
          padding: var(--xl-spacer);
        }
      }

      @media (min-width: 1200px) {
        aside::after {
          padding: 80%;
          top: 80px;
          right: 70px;
          transform: none;
        }
      }

      aside > * + * {
        border-top: 1px solid var(--color-border);
      }

      aside > *:first-child {
        border-radius: var(--border-radius) var(--border-radius) 0 0;
      }

      aside > *:last-child {
        border-radius: 0 0 var(--border-radius) var(--border-radius);
      }

      /* variation for aside rendered in main content */
      #LinksCategory {
        column-gap: var(--m-spacer);
      }

      #LinksCategory > * {
        border: none;
        flex: 1 1 auto;
      }

      #LinksCategory umb-market-package-sidebar-category {
        margin-top: var(--m-spacer);
      }

      @media (min-width: 767px) {
        #LinksCategory {
          display: flex;
        }

        #LinksCategory umb-market-package-sidebar-category {
          max-width: 25%;
          margin-top: 0;
        }
      }
    `,
  ];
}
